import { useId, useRef, useState } from "react";
import { Copy, Check, Icon, CircleAlert, CircleCheck } from "lucide-react";
import { Button } from "@/toggle-ui-kit/button";
import { toast } from "sonner";
import { RowItemV2, TableItemsV2 } from "@/apis/preview/v2/types";
import {
  TableUnitCaption,
  toTableUnitCaptionProps,
} from "@/components/common/table-unit-caption";
import { ArrowedButtonTooltip } from "@/components/common/tooltip";
import { IxdTable, TONE_CELL_BG, TONE_TEXT } from "@/components/ixd-table";
import {
  selectableCellAttrs,
  useTableCellSelection,
} from "@/hooks/use-table-cell-selection";
import { useUpdateTransposeState } from "../sidebar/hook";
import { cn } from "@/lib/utils";
import { extendedTagClass } from "@/components/extended-tag-style";
import { ATTR } from "@/utils/dom-attrs";
import { useWorkbookCellNotice } from "./workbook-cell-notice-context";
import { TableScrollArea } from "@/toggle-ui-kit/table-scroll-area";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import { BalanceTypeChip } from "./balance-type-chip";
import { copyTableToClipboard, TransposeToggle } from "./table-utils";

const SUM_MISMATCH_LABEL = "계산된 합계열은 태깅된 합계열의 값과 다릅니다.";

type CellHandlers = {
  onMouseDown?: (event: React.MouseEvent) => void;
  onMouseEnter?: (event: React.MouseEvent) => void;
  onDoubleClick?: () => void;
};

const copyCellValue = async (value: string) => {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    toast.success(
      `"${value.length > 20 ? value.substring(0, 20) + "..." : value}"이(가) 복사되었습니다.`,
    );
  } catch (error) {
    console.error("복사 실패:", error);
  }
};

const spanProps = (item: RowItemV2) => ({
  colSpan: item.colspan > 1 ? item.colspan : undefined,
  rowSpan: item.rowspan > 1 ? item.rowspan : undefined,
});

const cellHandlers = (
  item: RowItemV2,
  uniqueId: string,
  onCellMouseDown?: (uniqueId: string, event: React.MouseEvent) => void,
  onCellMouseEnter?: (event: React.MouseEvent) => void,
): CellHandlers => {
  if (item.type === "th") return {};
  return {
    onMouseDown: (event) => {
      if (!onCellMouseDown) return;
      event.preventDefault();
      onCellMouseDown(uniqueId, event);
    },
    onMouseEnter: (event) => {
      if (!onCellMouseEnter) return;
      const isLeftButtonPressed =
        event.buttons === 1 || event.nativeEvent.buttons === 1;
      if (!isLeftButtonPressed) return;
      event.preventDefault();
      onCellMouseEnter(event);
    },
    onDoubleClick: () => copyCellValue(item.value),
  };
};

interface CellVariantProps {
  item: RowItemV2;
  rowIndex: number;
  cellIndex: number;
  columnHeaderRowCount: number;
  uniqueId: string;
  handlers: CellHandlers;
}

const IxdCell = ({
  item,
  rowIndex,
  cellIndex,
  columnHeaderRowCount,
  uniqueId,
  handlers,
  period,
}: CellVariantProps & { period: "CURRENT" | "PRIOR" }) => {
  const {
    value,
    type,
    isSumValueDifferentWithPreviousValue,
    isSumValueSame,
    balanceType,
    id,
    isAxisTotal,
    isExtended,
    // 값 동기화 실패를 이 셀에 표시하려면 워크북 셀 주소가 DOM에 있어야 한다.
    sourceDsdAddress,
  } = item;
  // IXD 쪽 미반영 표식과 같은 컴포넌트를 쓴다 — 같은 뜻이면 같은 모양이어야 한다.
  // 주소가 없는 실패는 셀의 태그 키(`id`)로 찾으므로 기간과 함께 넘긴다.
  const { notice: cellNotice, onNoticeClick } = useWorkbookCellNotice(
    sourceDsdAddress,
    id,
    period,
  );

  if (type === "th") {
    return (
      <IxdTable.Header
        {...spanProps(item)}
        scope={
          rowIndex < columnHeaderRowCount
            ? "col"
            : cellIndex === 0
              ? "row"
              : undefined
        }
        period={period === "CURRENT" ? "당기" : "전기"}
        chipPlacement="bottom"
        showDebitCreditChip={balanceType !== null}
        debitCreditValue={balanceType === "CREDIT" ? "대변" : "차변"}
        className="min-w-[120px] max-w-[240px]"
      >
        <span
          className={cn(
            "whitespace-pre-wrap break-words [overflow-wrap:anywhere]",
            isExtended && extendedTagClass("th"),
          )}
        >
          {value}
        </span>
      </IxdTable.Header>
    );
  }

  return (
    <IxdTable.Cell
      {...spanProps(item)}
      {...handlers}
      matchResult="default"
      id={uniqueId}
      data-cell-id={id || uniqueId}
      data-display-value={value}
      {...{ [ATTR.axisTotal]: isAxisTotal ? "" : undefined }}
      data-source-dsd-address={sourceDsdAddress ?? undefined}
      // 반영불가 패널이 "이 항목" 을 누르면 이 키로 셀을 찾아 데려간다.
      data-notice-key={cellNotice?.key}
      showNoticeMarker={!!cellNotice}
      noticeTone={cellNotice?.tone}
      title={cellNotice?.reason}
      onClick={
        cellNotice && onNoticeClick
          ? (event: React.MouseEvent<HTMLTableCellElement>) =>
              onNoticeClick(cellNotice, event.currentTarget)
          : undefined
      }
      className={cn(
        "min-w-[120px] max-w-[240px]",
        (isSumValueSame || isSumValueDifferentWithPreviousValue) &&
          "min-w-[184px]",
        isSumValueDifferentWithPreviousValue && "bg-red-100",
        // 반영불가 셀은 바탕째로 물들인다 — 표를 훑을 때 어느 칸이 막혔는지
        // 사유를 읽기 전에 먼저 보인다(핸드아웃 시안).
        cellNotice && TONE_CELL_BG[cellNotice.tone],
        // 패널에서 이 셀로 데려온 직후 잠깐 테두리를 둘러 어디로 왔는지 알린다.
        "data-[notice-focused]:ring-2 data-[notice-focused]:ring-red-500 data-[notice-focused]:ring-inset",
        "cursor-cell transition-colors duration-200 select-none",
      )}
    >
      <div className="flex min-w-0 items-center justify-end gap-1">
        {isSumValueDifferentWithPreviousValue && (
          <Tooltip label={SUM_MISMATCH_LABEL} side="bottom">
            <span className="inline-flex shrink-0">
              <Icon className="size-4 text-black" iconNode={[]}>
                <CircleAlert className="text-status-negative" />
              </Icon>
            </span>
          </Tooltip>
        )}
        {isSumValueSame && (
          <Icon className="size-4 shrink-0 text-black" iconNode={[]}>
            <CircleCheck className="text-status-positive" />
          </Icon>
        )}
        <span
          className={cn(
            "min-w-0 break-words [overflow-wrap:anywhere]",
            isExtended && extendedTagClass("td"),
          )}
        >
          {value}
        </span>
      </div>
      {/* 반영불가 사유는 셀에 상주한다 — 표식만으론 눌러보기 전까지 알 수 없다.
          전문은 같은 패널(CellLinkagePopover)이 맡으므로 여기선 짧은 말만. */}
      {cellNotice && onNoticeClick && (
        <button
          type="button"
          // 셀 드래그 선택이 칩 클릭에 끌려 들어가지 않게 끊는다.
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            // 앵커는 항상 셀 — 칩을 눌러도 셀 본문을 눌러도 패널이 같은 자리에 뜬다.
            onNoticeClick(
              cellNotice,
              event.currentTarget.closest("td") ?? event.currentTarget,
            );
          }}
          className={cn(
            "mt-0.5 block w-full truncate text-right caption-2-medium underline-offset-2 hover:underline",
            // 표식과 같은 tone 표를 쓴다 — 색이 어긋나면 같은 셀이 두 말을 한다.
            TONE_TEXT[cellNotice.tone],
          )}
          aria-label={`반영불가: ${cellNotice.shortReason} \u2014 자세히 보기`}
        >
          {cellNotice.shortReason}
        </button>
      )}
    </IxdTable.Cell>
  );
};

const PreviewCell = ({
  item,
  uniqueId,
  handlers,
  transpose,
}: CellVariantProps & { transpose: boolean }) => {
  const {
    value,
    type,
    isSumValueDifferentWithPreviousValue,
    isSumValueSame,
    balanceType,
    id,
    isAxisTotal,
    sourceDsdAddress,
  } = item;

  const backgroundStyle = () => {
    if (type === "th") return "bg-neutral-50";
    if (isSumValueDifferentWithPreviousValue) return "bg-red-100";
    return "bg-white";
  };

  const cellProps = {
    ...spanProps(item),
    ...handlers,
    className: cn(
      "border border-neutral-200 text-center text-primary h-[inherit]",
      backgroundStyle(),
      type !== "th" && "text-right",
      "whitespace-normal break-words",
      "min-w-[120px] max-w-[400px] px-3 py-[6px] label-1-normal-regular",
      type !== "th" &&
        "cursor-cell hover:bg-blue-50 transition-colors duration-200 select-none",
    ),
  };

  if (type === "th") {
    return (
      <th {...cellProps}>
        <div
          className={cn(
            "flex gap-1",
            transpose
              ? "flex-col items-start h-full"
              : "flex-row justify-between",
          )}
        >
          <div className="flex-1 text-wrap text-left">{value}</div>
          <div className="flex-0">
            <BalanceTypeChip balanceType={balanceType} />
          </div>
        </div>
      </th>
    );
  }

  return (
    <td
      {...cellProps}
      {...selectableCellAttrs({
        uniqueId,
        cellId: id || uniqueId,
        displayValue: value,
      })}
      {...{ [ATTR.axisTotal]: isAxisTotal ? "" : undefined }}
      data-source-dsd-address={sourceDsdAddress ?? undefined}
    >
      <div className="flex items-center justify-end gap-1">
        {isSumValueDifferentWithPreviousValue && (
          <ArrowedButtonTooltip label={SUM_MISMATCH_LABEL} side="bottom">
            <Icon className="text-black w-[16px] h-[16px]" iconNode={[]}>
              <CircleAlert className="text-red-500" />
            </Icon>
          </ArrowedButtonTooltip>
        )}
        {isSumValueSame && (
          <Icon className="text-black w-[16px] h-[16px]" iconNode={[]}>
            <CircleCheck className="text-green-500" />
          </Icon>
        )}
        <span>{value}</span>
      </div>
    </td>
  );
};

interface TableCellProps {
  item: RowItemV2;
  rowIndex: number;
  cellIndex: number;
  columnHeaderRowCount: number;
  uniqueId: string;
  onCellMouseDown?: (uniqueId: string, event: React.MouseEvent) => void;
  onCellMouseEnter?: (event: React.MouseEvent) => void;
  transpose: boolean;
  period: "CURRENT" | "PRIOR";
  density?: "default" | "compact";
}

const TableCell: React.FC<TableCellProps> = ({
  item,
  rowIndex,
  cellIndex,
  columnHeaderRowCount,
  uniqueId,
  onCellMouseDown,
  onCellMouseEnter,
  transpose,
  period,
  density = "default",
}) => {
  const handlers = cellHandlers(
    item,
    uniqueId,
    onCellMouseDown,
    onCellMouseEnter,
  );

  return density === "compact" ? (
    <IxdCell
      item={item}
      rowIndex={rowIndex}
      cellIndex={cellIndex}
      columnHeaderRowCount={columnHeaderRowCount}
      uniqueId={uniqueId}
      handlers={handlers}
      period={period}
    />
  ) : (
    <PreviewCell
      item={item}
      rowIndex={rowIndex}
      cellIndex={cellIndex}
      columnHeaderRowCount={columnHeaderRowCount}
      uniqueId={uniqueId}
      handlers={handlers}
      transpose={transpose}
    />
  );
};

interface TableWithCopyProps {
  table: TableItemsV2;
  tableIndex: number;
  period: "CURRENT" | "PRIOR";
  density?: "default" | "compact";
}

export const TableWithCopy = ({
  table,
  tableIndex,
  period,
  density = "default",
}: TableWithCopyProps) => {
  const [copied, setCopied] = useState(false);
  const instanceId = useId();
  const tableRef = useRef<HTMLDivElement>(null);

  const { handleCellMouseDown, handleCellMouseEnter } = useTableCellSelection(
    tableRef,
    {
      styleId: `preview-cell-selection-${instanceId}-${tableIndex}`,
    },
  );

  const { updateTransposeStateMutate } = useUpdateTransposeState();
  const firstValueRowIndex = table.rowItems.findIndex((row) =>
    row.some((item) => item.type !== "th"),
  );
  const columnHeaderRowCount =
    firstValueRowIndex === -1 ? table.rowItems.length : firstValueRowIndex;

  const handleCopy = async () => {
    if (!tableRef.current) return;

    const tableElement = tableRef.current.querySelector(
      "table:not([data-sticky-header-clone])",
    ) as HTMLTableElement;
    if (!tableElement) {
      toast.error("복사 실패: 테이블을 찾을 수 없습니다.");
      return;
    }

    const success = await copyTableToClipboard(tableElement);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("테이블의 내용이 복사되었습니다.");
    } else {
      toast.error(
        "복사 실패: 테이블의 내용을 복사하는 중 오류가 발생했습니다.",
      );
    }
  };

  const tableElement = (
    <table
      className={cn(
        "border-collapse select-none",
        density === "compact" ? "w-max" : "border border-gray-300",
      )}
    >
      <tbody>
        {table.rowItems.map((row: RowItemV2[], rowIndex: number) => (
          <tr
            key={`${tableIndex}-row-${rowIndex}`}
            className={density === "default" ? "h-px" : undefined}
          >
            {row.map((item, cellIndex) => {
              const uniqueId = `${instanceId}-r${rowIndex}-c${cellIndex}`;
              return (
                <TableCell
                  key={`${tableIndex}-${rowIndex}-${cellIndex}-${item.id}`}
                  item={item}
                  rowIndex={rowIndex}
                  cellIndex={cellIndex}
                  columnHeaderRowCount={columnHeaderRowCount}
                  uniqueId={uniqueId}
                  onCellMouseDown={handleCellMouseDown}
                  onCellMouseEnter={handleCellMouseEnter}
                  transpose={table.transpose}
                  period={period}
                  density={density}
                />
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="w-full max-w-full relative group min-w-0" ref={tableRef}>
      {density === "default" && (
        <>
          <div className="flex items-center justify-between pb-2">
            <TransposeToggle
              transpose={table.transpose}
              setTranspose={(transpose) => {
                updateTransposeStateMutate({
                  roleKey: table.roleKey,
                  transpose,
                });
              }}
            />
            <Button
              onClick={handleCopy}
              size={32}
              className="bg-white/90 hover:bg-white border shadow-none"
              leftIcon={
                copied ? (
                  <Check className="h-4 w-4 text-primary-base" />
                ) : (
                  <Copy className="h-4 w-4 text-label-alternative" />
                )
              }
            >
              {copied ? "복사됨" : "전체 복사"}
            </Button>
          </div>

          <div className="text-xs text-gray-500 mb-2 truncate">
            💡 <strong>사용법:</strong> 드래그하여 우측 복사 버튼을 클릭하여
            복사하거나, 각 셀을 더블클릭하면 셀 하나씩 복사할 수 있습니다.
            Ctrl+C (복사하기)
          </div>
        </>
      )}

      <div data-final-unit-label>
        <TableUnitCaption
          className="flex flex-row items-end justify-end mb-[12px]"
          {...toTableUnitCaptionProps(table)}
        />
      </div>

      {density === "compact" ? (
        <TableScrollArea>{tableElement}</TableScrollArea>
      ) : (
        <div className="w-full overflow-x-auto overflow-y-auto min-w-0 max-w-full">
          {tableElement}
        </div>
      )}
    </div>
  );
};
